import React, { useState } from 'react';
import { apiService as mockApi } from '@/api';
import { api } from '@/api/client';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store/appStore';
import { Button } from '@/components/ui/Button';
import { Input, Label, Checkbox } from '@/components/ui/Input';
import {
  Shield,
  Lock,
  Mail,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  Building2,
  Globe,
  ArrowRight,
  Sparkles,
  ExternalLink,
  X,
} from 'lucide-react';

export function Login() {
  const navigate = useNavigate();
  const { currentTenant, setCurrentTenant } = useAppStore();

  // Form State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenant, setTenant] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  // Modals & UI States
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitted, setForgotSubmitted] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const [showSsoModal, setShowSsoModal] = useState(false);
  const [selectedSsoProvider, setSelectedSsoProvider] = useState<'azure' | 'okta' | 'google' | 'saml'>('azure');
  const [ssoLoading, setSsoLoading] = useState(false);

  // Form Validation
  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      errors.email = 'Email address is required';
    } else if (!emailRegex.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Standard Login Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Authenticate via API (mock or real depending on env)
      const result = await mockApi.login(email, password, tenant.trim() || undefined);
      const token = result?.accessToken || result?.token;
      if (!token) {
        setErrorMessage('Login succeeded but no access token was returned.');
        return;
      }
      api.setTokens(token, result?.refreshToken);
      localStorage.setItem('insightnode_token', token);

      if (rememberMe) {
        localStorage.setItem('insightnode_saved_email', email);
      } else {
        localStorage.removeItem('insightnode_saved_email');
      }

      navigate('/');
    } catch (err: any) {
      setErrorMessage(err.message || 'Authentication failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  // Quick Demo Autofill Helper
  const handleFillDemoCredentials = () => {
    setEmail('admin@insightnode.io');
    setPassword('InsightNode2026!');
    setFieldErrors({});
    setErrorMessage(null);
  };

  // Handle Forgot Password Submit
  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail || !/\S+@\S+\.\S+/.test(forgotEmail)) {
      return;
    }
    setForgotLoading(true);
    await new Promise((r) => setTimeout(r, 700));
    setForgotLoading(false);
    setForgotSubmitted(true);
  };

  // Handle SSO Login Submit
  const handleSsoSubmit = async () => {
    setSsoLoading(true);
    await new Promise((r) => setTimeout(r, 1000));
    setSsoLoading(false);
    setShowSsoModal(false);
    navigate('/');
  };

  return (
    <div className="min-h-screen w-full bg-[var(--bg)] text-[var(--text-primary)] flex flex-col justify-between selection:bg-[var(--accent)]/30 selection:text-[var(--text-primary)] relative overflow-x-hidden font-sans">
      {/* Background Subtle Geometry & Grid Accent */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-blue-950/20 via-transparent to-transparent pointer-events-none" />
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none" 
        style={{
          backgroundImage: `linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }} 
      />

      {/* Top Header Bar */}
      <header className="w-full px-6 py-4 border-b border-[var(--border)] flex items-center justify-between relative z-10 bg-[var(--bg)]/80 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[var(--accent)] flex items-center justify-center font-bold text-white shadow-sm text-lg">
            I
          </div>
          <span className="font-semibold text-lg tracking-tight text-[var(--text-primary)]">InsightNode</span>
          <span className="text-xs px-2 py-0.5 rounded bg-[var(--elevated)] border border-[var(--border)] text-[var(--text-muted)] font-mono">
            RMM v4.2
          </span>
        </div>

        <div className="flex items-center gap-4 text-xs text-[var(--text-muted)]">
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--panel)] border border-[var(--border)]">
            <span className="h-2 w-2 rounded-full bg-[var(--status-online)] animate-pulse" />
            <span className="text-[var(--text-secondary)]">All Security Systems Operational</span>
          </div>
          <a
            href="https://docs.insightnode.io"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          >
            <span>Documentation</span>
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </header>

      {/* Main Centered Content Container */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 md:p-8 relative z-10 my-8">
        <div className="w-full max-w-md space-y-6">
          
          {/* Main Card Container */}
          <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg p-6 sm:p-8 shadow-2xl relative">
            
            {/* Header / Logo */}
            <div className="flex flex-col items-center text-center mb-6">
              <div className="h-12 w-12 rounded-lg bg-[var(--accent)] flex items-center justify-center font-extrabold text-white text-2xl shadow-lg shadow-blue-500/20 mb-3">
                I
              </div>
              <h1 className="text-xl font-bold tracking-tight text-[var(--text-primary)]">
                InsightNode RMM
              </h1>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Centralized Remote Monitoring & Management Platform
              </p>
            </div>

            {/* Error Message Alert */}
            {errorMessage && (
              <div className="mb-5 p-3.5 rounded-md bg-[var(--status-critical)]/10 border border-[var(--status-critical)]/30 text-[var(--status-critical)] text-xs flex items-start gap-2.5">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium">Authentication Error</p>
                  <p className="text-[var(--text-secondary)] mt-0.5">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Tenant slug */}
              <div>
                <Label htmlFor="tenant">Organization tenant</Label>
                <div className="relative">
                  <Input
                    id="tenant"
                    type="text"
                    placeholder="Leave blank if you have only one tenant"
                    value={tenant}
                    onChange={(e) => {
                      setTenant(e.target.value);
                      setCurrentTenant(e.target.value || 'default');
                    }}
                    className="w-full bg-[var(--bg)] border border-[var(--border)] text-[var(--text-primary)] rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors"
                    autoComplete="organization"
                  />
                  <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-[var(--text-muted)]" />
                </div>
              </div>

              {/* Email Address Input */}
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@organization.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    className={`w-full bg-[var(--bg)] border ${
                      fieldErrors.email ? 'border-[var(--status-critical)]' : 'border-[var(--border)]'
                    } text-[var(--text-primary)] rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors`}
                    autoComplete="email"
                  />
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-[var(--text-muted)]" />
                </div>
                {fieldErrors.email && (
                  <p className="text-[2xs] text-[var(--status-critical)] mt-1">{fieldErrors.email}</p>
                )}
              </div>

              {/* Password Input */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="password" className="mb-0">Password</Label>
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(true)}
                    className="text-xs text-[var(--accent)] hover:underline focus:outline-none"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your administrator password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: undefined }));
                    }}
                    className={`w-full bg-[var(--bg)] border ${
                      fieldErrors.password ? 'border-[var(--status-critical)]' : 'border-[var(--border)]'
                    } text-[var(--text-primary)] rounded-md pl-9 pr-10 py-2 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors`}
                    autoComplete="current-password"
                  />
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-[var(--text-muted)]" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] focus:outline-none"
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="text-[2xs] text-[var(--status-critical)] mt-1">{fieldErrors.password}</p>
                )}
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center justify-between pt-1">
                <Checkbox
                  checked={rememberMe}
                  onChange={setRememberMe}
                  label="Remember this device"
                />
                <span className="text-[2xs] text-[var(--text-muted)] font-mono">IP: 192.168.1.100</span>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2.5 pt-2">
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  className="w-full h-10 bg-[var(--accent)] hover:bg-blue-600 text-white font-medium rounded-md flex items-center justify-center gap-2 transition-colors cursor-pointer"
                >
                  {!isLoading && <span>Sign in to Console</span>}
                  {!isLoading && <ArrowRight className="h-4 w-4" />}
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowSsoModal(true)}
                  className="w-full h-10 bg-[var(--elevated)] hover:bg-[var(--hover)] text-[var(--text-primary)] border border-[var(--border)] font-medium rounded-md flex items-center justify-center gap-2 transition-colors"
                >
                  <KeyRound className="h-4 w-4 text-[var(--text-secondary)]" />
                  <span>SSO Login</span>
                </Button>
              </div>
            </form>

            {/* Quick Demo Helper */}
            <div className="mt-5 pt-4 border-t border-[var(--border)] flex items-center justify-between text-xs text-[var(--text-muted)]">
              <span>Testing the platform?</span>
              <button
                type="button"
                onClick={handleFillDemoCredentials}
                className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] flex items-center gap-1 font-mono hover:underline"
              >
                <Sparkles className="h-3 w-3 text-[var(--status-warning)]" />
                <span>Fill Demo Credentials</span>
              </button>
            </div>
          </div>

          {/* Security & Access Banner Text */}
          <div className="text-center space-y-3">
            <p className="text-xs text-[var(--text-muted)] leading-relaxed max-w-sm mx-auto">
              Only authorized administrators can access this system.
            </p>

            <div className="flex items-center justify-center gap-4 text-[2xs] text-[var(--text-muted)] pt-1">
              <span className="flex items-center gap-1">
                <Shield className="h-3 w-3 text-[var(--status-online)]" />
                <span>256-Bit AES Encryption</span>
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Lock className="h-3 w-3 text-[var(--accent)]" />
                <span>MFA Enforced</span>
              </span>
              <span>•</span>
              <span>SOC 2 Type II</span>
            </div>
          </div>

        </div>
      </main>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg w-full max-w-md p-6 relative space-y-4 shadow-2xl">
            <button
              onClick={() => {
                setShowForgotModal(false);
                setForgotSubmitted(false);
              }}
              className="absolute right-4 top-4 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-[var(--accent)]/15 border border-[var(--accent)]/30 flex items-center justify-center text-[var(--accent)]">
                <KeyRound className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-[var(--text-primary)]">Reset Administrator Password</h3>
                <p className="text-xs text-[var(--text-muted)]">Instructions will be sent to your registered email</p>
              </div>
            </div>

            {forgotSubmitted ? (
              <div className="p-4 rounded-md bg-[var(--status-online)]/10 border border-[var(--status-online)]/30 text-xs space-y-2">
                <div className="flex items-center gap-2 text-[var(--status-online)] font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Reset Link Sent</span>
                </div>
                <p className="text-[var(--text-secondary)]">
                  If an account exists for <span className="font-mono text-white">{forgotEmail}</span>, you will receive password reset instructions within 2 minutes.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => {
                    setShowForgotModal(false);
                    setForgotSubmitted(false);
                  }}
                >
                  Return to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4 pt-2">
                <div>
                  <Label htmlFor="forgot-email">Administrator Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="admin@organization.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="w-full bg-[var(--bg)] border border-[var(--border)] text-sm"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowForgotModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    loading={forgotLoading}
                  >
                    Send Reset Link
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* SSO Login Modal */}
      {showSsoModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[var(--panel)] border border-[var(--border)] rounded-lg w-full max-w-md p-6 relative space-y-5 shadow-2xl">
            <button
              onClick={() => setShowSsoModal(false)}
              className="absolute right-4 top-4 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-[var(--accent)]">
                <Globe className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-[var(--text-primary)]">Enterprise Single Sign-On</h3>
                <p className="text-xs text-[var(--text-muted)]">Authenticate using your identity provider</p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs text-[var(--text-secondary)] font-medium">Select Identity Provider:</p>
              
              <div
                onClick={() => setSelectedSsoProvider('azure')}
                className={`p-3 rounded-md border flex items-center justify-between cursor-pointer transition-colors ${
                  selectedSsoProvider === 'azure'
                    ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
                    : 'border-[var(--border)] bg-[var(--bg)] text-[var(--text-secondary)] hover:bg-[var(--elevated)]'
                }`}
              >
                <div className="flex items-center gap-3 text-xs font-medium">
                  <div className="h-6 w-6 rounded bg-blue-600/20 text-blue-400 flex items-center justify-center font-bold">MS</div>
                  <span>Microsoft Entra ID (Azure AD)</span>
                </div>
                {selectedSsoProvider === 'azure' && <CheckCircle2 className="h-4 w-4 text-[var(--accent)]" />}
              </div>

              <div
                onClick={() => setSelectedSsoProvider('okta')}
                className={`p-3 rounded-md border flex items-center justify-between cursor-pointer transition-colors ${
                  selectedSsoProvider === 'okta'
                    ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
                    : 'border-[var(--border)] bg-[var(--bg)] text-[var(--text-secondary)] hover:bg-[var(--elevated)]'
                }`}
              >
                <div className="flex items-center gap-3 text-xs font-medium">
                  <div className="h-6 w-6 rounded bg-indigo-600/20 text-indigo-400 flex items-center justify-center font-bold">OK</div>
                  <span>Okta Workforce Identity</span>
                </div>
                {selectedSsoProvider === 'okta' && <CheckCircle2 className="h-4 w-4 text-[var(--accent)]" />}
              </div>

              <div
                onClick={() => setSelectedSsoProvider('google')}
                className={`p-3 rounded-md border flex items-center justify-between cursor-pointer transition-colors ${
                  selectedSsoProvider === 'google'
                    ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
                    : 'border-[var(--border)] bg-[var(--bg)] text-[var(--text-secondary)] hover:bg-[var(--elevated)]'
                }`}
              >
                <div className="flex items-center gap-3 text-xs font-medium">
                  <div className="h-6 w-6 rounded bg-red-600/20 text-red-400 flex items-center justify-center font-bold">G</div>
                  <span>Google Workspace SSO</span>
                </div>
                {selectedSsoProvider === 'google' && <CheckCircle2 className="h-4 w-4 text-[var(--accent)]" />}
              </div>

              <div
                onClick={() => setSelectedSsoProvider('saml')}
                className={`p-3 rounded-md border flex items-center justify-between cursor-pointer transition-colors ${
                  selectedSsoProvider === 'saml'
                    ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text-primary)]'
                    : 'border-[var(--border)] bg-[var(--bg)] text-[var(--text-secondary)] hover:bg-[var(--elevated)]'
                }`}
              >
                <div className="flex items-center gap-3 text-xs font-medium">
                  <div className="h-6 w-6 rounded bg-gray-600/20 text-gray-300 flex items-center justify-center font-bold">S</div>
                  <span>Custom SAML 2.0 / PingIdentity</span>
                </div>
                {selectedSsoProvider === 'saml' && <CheckCircle2 className="h-4 w-4 text-[var(--accent)]" />}
              </div>
            </div>

            <div className="pt-2 flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSsoModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={ssoLoading}
                onClick={handleSsoSubmit}
                className="bg-[var(--accent)]"
              >
                Continue to Identity Provider
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="w-full px-6 py-4 border-t border-[var(--border)] flex flex-col sm:flex-row items-center justify-between text-xs text-[var(--text-muted)] gap-2 relative z-10 bg-[var(--bg)]/80">
        <div>
          © 2026 InsightNode Technologies Inc. All rights reserved.
        </div>
        <div className="flex items-center gap-4 text-[2xs]">
          <a href="#" className="hover:text-[var(--text-primary)] transition-colors">Privacy Policy</a>
          <span>•</span>
          <a href="#" className="hover:text-[var(--text-primary)] transition-colors">Terms of Service</a>
          <span>•</span>
          <a href="#" className="hover:text-[var(--text-primary)] transition-colors">Security Disclosures</a>
        </div>
      </footer>
    </div>
  );
}

export default Login;
